import { Fragment, useMemo } from 'react'
import { Link } from 'react-router-dom'
import {
  ConferenceBadge,
  DelinquencyNote,
  EmptyState,
  PlayerCell,
  RowLink,
  RowLinkTarget,
  SeedCutRow,
  SortableHeaderRow,
  SortedAwayNote,
  StreakCell,
  TiebreakerFlag,
  TiebreakerFootnote,
  describeDelinquency,
  formatPct,
  formatRecord,
  formatSharePct,
  formatSigned,
  playerProfilePath,
  playerSeasonGamesPath,
  recordSortKey,
  streakSortValue,
  useTableSort,
  type SortableColumn,
} from './ui'
import type { StandingsRow } from '../types'

/**
 * One standings table, shared by the standings page and the home previews.
 *
 * @param rows - Players in the order the server produced them. For a
 * conference that is the league's seeded order, tiebreakers already applied.
 * @param seeded - Whether `rows` is a seeding rather than a plain ranking. Only
 * a single conference is; the combined table shares its row objects with the
 * conference lists and their tiebreaker notes explain a conference seed rather
 * than a position there.
 * @param showConference - Adds the Conf column, for a table mixing both.
 * @param showGamesLink - Adds the trailing button column. The home previews
 * leave it off to stay narrow; the record cell links to the same place either
 * way.
 * @param seasonNumber - Season these rows describe, so a row opens that
 * player's results for *this* season rather than the current one.
 *
 * @remarks
 * Home and Standings render the identical markup from this one component
 * specifically so the seeding safeguard below cannot be implemented correctly
 * in one of them and wrongly in the other.
 *
 * ## Two destinations in one row
 *
 * The row goes to this player's results for this season, which is what it has
 * always done. The name goes to the player's career profile. Those are
 * different pages, so the name cannot double as the row's keyboard path the way
 * it used to, and it cannot be nested inside a row-wide anchor either — see
 * {@link RowLink}. The record cell carries the row's destination instead
 * ({@link RowLinkTarget}), which keeps both reachable by Tab, keeps every
 * anchor a sibling of the others, and adds no column to a narrow preview.
 *
 * ## The seeding safeguard
 *
 * Seed numbers, the first-round-bye hairline, the playoff line, the `†`
 * markers and their footnote are all claims about *where a row sits*, and every
 * one of them is only true of the server's ordering. Sorting the table by
 * Points For and leaving the playoff line where it was would assert that the
 * six highest-scoring players qualify, which is false.
 *
 * So all five are driven by a single `inSeedOrder` boolean — seeded rows that
 * the visitor has not re-sorted — and the table says plainly that it is off
 * seed order while offering one click back. There is deliberately no path that
 * re-derives a cut line for a sorted view: the cut exists in one ordering only.
 */
export default function StandingsTable({
  rows,
  showConference = false,
  showGamesLink = false,
  seeded = false,
  seasonNumber,
}: {
  rows: StandingsRow[]
  showConference?: boolean
  showGamesLink?: boolean
  seeded?: boolean
  seasonNumber?: number
}) {
  const columns = useMemo<Array<SortableColumn<StandingsRow>>>(
    () => [
      { key: 'rank', label: '#', className: 'rank' },
      { key: 'player', label: 'Player', sort: row => row.displayName },
      ...(showConference
        ? [
            {
              key: 'conference',
              label: 'Conf',
              sortLabel: 'Conference',
              sort: (row: StandingsRow) => row.conference,
            },
          ]
        : []),
      {
        key: 'gamesPlayed',
        label: 'GP',
        className: 'num',
        sortLabel: 'games played',
        sort: row => row.gamesPlayed,
      },
      {
        key: 'record',
        label: 'Record',
        className: 'record',
        hint: 'Sort by wins, then by fewest losses',
        sort: row => recordSortKey(row.wins, row.losses),
      },
      {
        key: 'pct',
        label: 'Pct',
        className: 'num',
        sortLabel: 'win percentage',
        // A player with no games renders .000 but has not earned it; treat it
        // as absent so the unplayed block groups at the end either way.
        sort: row => (row.gamesPlayed === 0 ? null : row.winPercentage),
      },
      {
        key: 'streak',
        label: 'Streak',
        className: 'num',
        hint: 'Sort by run length — longest winning streak to longest losing streak',
        sort: row => streakSortValue(row.streak),
      },
      {
        key: 'pointsFor',
        label: 'PF',
        className: 'num',
        sortLabel: 'points for',
        sort: row => row.pointsFor,
      },
      {
        key: 'pointsAgainst',
        label: 'PA',
        className: 'num',
        sortLabel: 'points against',
        sort: row => row.pointsAgainst,
      },
      {
        key: 'diff',
        label: 'Diff',
        className: 'num',
        sortLabel: 'point differential',
        sort: row => row.pointDifferential,
      },
      {
        key: 'p1Pct',
        label: 'P1%',
        className: 'num',
        sortLabel: 'P1 share',
        hint: 'Share of games this player had the P1 controller',
        // 0% and "no games at all" are different claims; a player yet to play
        // sorts with the other unplayed rows rather than below everyone.
        sort: row => (row.gamesPlayed === 0 ? null : row.p1Percentage),
      },
      ...(showGamesLink ? [{ key: 'games', className: 'num' }] : []),
    ],
    [showConference, showGamesLink]
  )

  const sort = useTableSort(rows, columns)
  const inSeedOrder = seeded && sort.isNatural

  // The rank cell is a seed on a seeded table, so it goes away with the rest of
  // the seeding furniture. On an unseeded ranking it is only a row counter and
  // renumbering it against the new order is honest.
  const showRank = !seeded || inSeedOrder
  const visibleColumns = showRank ? columns : columns.filter(column => column.key !== 'rank')

  if (rows.length === 0) {
    return (
      <EmptyState title="No players here yet">
        Conference assignments are managed by an admin.
      </EmptyState>
    )
  }

  return (
    <>
      {seeded && !sort.isNatural && (
        <SortedAwayNote
          label={sort.activeLabel ?? 'a column'}
          onReset={sort.reset}
          resetLabel="Back to seed order"
        >
          Seeds, the playoff line and tiebreaker markers only describe the league&rsquo;s
          seeding, so they are hidden here.
        </SortedAwayNote>
      )}

      <div className="table-wrap">
        <table className="data">
          <thead>
            <SortableHeaderRow columns={visibleColumns} sort={sort} />
          </thead>
          <tbody>
            {sort.rows.map((row, index) => {
              const gamesPath =
                row.userId === null ? null : playerSeasonGamesPath(row.userId, seasonNumber)

              return (
                <Fragment key={row.userId ?? row.displayName}>
                  <RowLink to={gamesPath}>
                    {showRank && <td className="rank">{index + 1}</td>}
                    <td>
                      <PlayerCell
                        name={row.displayName}
                        discordId={row.discordId}
                        avatar={row.discordAvatar}
                        to={row.userId === null ? undefined : playerProfilePath(row.userId)}
                        flag={describeDelinquency(row.delinquency)}
                      />
                    </td>
                    {showConference && (
                      <td>
                        <ConferenceBadge conference={row.conference} />
                      </td>
                    )}
                    <td className="num">{row.gamesPlayed}</td>
                    <td className="record">
                      <RowLinkTarget
                        to={gamesPath}
                        label={`${row.displayName} — season games`}
                      >
                        {formatRecord(row.wins, row.losses, row.ties)}
                      </RowLinkTarget>
                      {inSeedOrder && <TiebreakerFlag note={row.tiebreaker} />}
                    </td>
                    <td className="num">{formatPct(row.winPercentage)}</td>
                    <td className="num">
                      <StreakCell streak={row.streak} />
                    </td>
                    <td className="num">{row.pointsFor}</td>
                    <td className="num">{row.pointsAgainst}</td>
                    <td className="num">{formatSigned(row.pointDifferential)}</td>
                    <td className="num" title={`${row.p1Games} of ${row.gamesPlayed} as P1`}>
                      {formatSharePct(row.gamesPlayed === 0 ? null : row.p1Percentage)}
                    </td>
                    {showGamesLink && (
                      <td className="num">
                        {gamesPath && (
                          <Link
                            className="btn btn--ghost btn--sm"
                            to={gamesPath}
                            aria-label={`${row.displayName} — season games`}
                          >
                            Games
                          </Link>
                        )}
                      </td>
                    )}
                  </RowLink>
                  {inSeedOrder && (
                    <SeedCutRow
                      index={index}
                      rowCount={sort.rows.length}
                      columnCount={visibleColumns.length}
                    />
                  )}
                </Fragment>
              )
            })}
          </tbody>
        </table>
      </div>
      {inSeedOrder && <TiebreakerFootnote rows={sort.rows} />}
      {/* Not gated on seed order: a missed deadline is a fact about the player
          rather than about where the row sits, so re-sorting cannot falsify it. */}
      <DelinquencyNote rows={sort.rows} />
    </>
  )
}
